import React, { useState, useRef, useMemo } from 'react';
import styles from '../assets/photoUpload.module.css';
import s3Service from '../services/s3Service';
import { OFFLINE_PHOTO_PREFIX } from '../lib/offlineDb';

const PhotoUpload = React.memo(({
  label,
  fieldName,
  onUpload,
  existingImage,
  existingImageName,
  existingImageUrl,
  onPreviewClick,
  // Controlled-mode rotation. Pass both to let the parent own the rotation
  // (so a sibling — e.g. the lightbox — can rotate the same image and the
  // thumbnail tracks it). If `onRotate` is not provided we keep the old
  // internal rotation state.
  rotation: controlledRotation,
  onRotate: controlledOnRotate,
  // Optional. Called with (fieldName, dataUrl) once a freshly-selected file
  // has been read locally. Lets the parent share the same preview the
  // thumbnail uses (e.g. for a lightbox), avoiding a hit on S3/CDN for an
  // image that hasn't propagated yet.
  onPreviewReady,
  // Optional. async (fieldName, file) => placeholderKey. When provided, a
  // selection made while offline (or after an online upload fails/times out) is
  // buffered locally via this callback instead of being lost. The returned
  // placeholder is stored as the field value until the sync engine replaces it
  // with the real S3 key. May throw if local storage is unavailable/full — in
  // which case the capture is aborted with a clear message (never silently lost).
  onOfflineSave,
}) => {
  const [preview, setPreview] = useState(null);
  const [internalRotation, setInternalRotation] = useState(0);
  const isRotationControlled = typeof controlledOnRotate === 'function';
  const rotation = isRotationControlled ? (controlledRotation || 0) : internalRotation;
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imageUrl, setImageUrl] = useState(null);
  const [hasTriedFallback, setHasTriedFallback] = useState(false);
  const fileInputRef = useRef(null);

  // Use presigned URL from backend if available, otherwise generate CDN URL
  const memoizedImageUrl = useMemo(() => {
    // Priority 1: Use presigned URL from backend (direct S3 presigned URL)
    if (existingImageUrl) {
      console.log(`✅ [PhotoUpload] Using presigned URL from backend for ${fieldName}:`, existingImageUrl.substring(0, 50) + '...');
      return existingImageUrl;
    }
    
    // Priority 2: Generate CDN URL from S3 key
    if (existingImage) {
      // Ensure existingImage is a string (not an object or array)
      const imageKey = typeof existingImage === 'string' ? existingImage : String(existingImage);
      const cdnUrl = s3Service.getFileUrl(imageKey);
      console.log(`🔍 [PhotoUpload] Generated CDN URL for ${fieldName} from S3 key "${imageKey}":`, cdnUrl);
      return cdnUrl;
    }
    
    console.log(`⚠️ [PhotoUpload] No image URL available for ${fieldName} (existingImageUrl: ${existingImageUrl}, existingImage: ${existingImage})`);
    return null;
  }, [existingImage, existingImageUrl, fieldName]);

  // Set image URL when memoized URL changes
  React.useEffect(() => {
    if (memoizedImageUrl) {
      console.log(`🖼️ [PhotoUpload] Setting imageUrl for ${fieldName}:`, memoizedImageUrl.substring(0, 50) + '...');
      setImageUrl(memoizedImageUrl);
      setHasTriedFallback(false); // Reset fallback flag when image changes
    } else {
      console.log(`🖼️ [PhotoUpload] Clearing imageUrl for ${fieldName}`);
      setImageUrl(null);
      setHasTriedFallback(false);
    }
  }, [memoizedImageUrl, fieldName]);

  // Show the freshly-selected file as the thumbnail/lightbox preview.
  const showPreview = (file) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPreview(ev.target.result);
      if (typeof onPreviewReady === 'function') {
        onPreviewReady(fieldName, ev.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  // Online S3 upload bounded by a timeout, so a dead zone / captive portal
  // (where navigator.onLine is still true) falls back to offline buffering
  // instead of hanging forever.
  const ONLINE_UPLOAD_TIMEOUT_MS = 30000;
  const uploadOnlineWithTimeout = (file, folder) =>
    new Promise((resolve, reject) => {
      const timer = setTimeout(
        () => reject(new Error('Upload timed out')),
        ONLINE_UPLOAD_TIMEOUT_MS
      );
      s3Service
        .uploadImage(file, {
          folder,
          onProgress: (progress) => setUploadProgress(progress),
        })
        .then((key) => {
          clearTimeout(timer);
          resolve(key);
        })
        .catch((err) => {
          clearTimeout(timer);
          reject(err);
        });
    });

  // Buffer the file for later sync. Returns true on success, false if the
  // capture had to be aborted (storage unavailable/full) — caller stops.
  const bufferOffline = async (file, folder) => {
    try {
      const placeholder = await onOfflineSave(file, { folder, field: fieldName });
      onUpload(fieldName, placeholder, existingImageName);
      showPreview(file);
      return true;
    } catch (offlineErr) {
      console.error(`❌ [PhotoUpload] Offline buffer failed for ${fieldName}:`, offlineErr);
      const full = /quota/i.test(offlineErr?.message || '') || offlineErr?.name === 'QuotaExceededError';
      alert(
        full
          ? 'This device is out of storage, so the photo could not be saved for offline upload. Free up space and try again.'
          : 'Offline storage is unavailable, so the photo cannot be saved while offline. Keep this tab open and try again once you are back online.'
      );
      return false;
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type up-front so online and offline paths behave identically.
    // Mirrors the allowed-types list in s3Service.uploadImage (the online path),
    // so a file buffered offline can never fail type validation later at sync.
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!ALLOWED_TYPES.includes(file.type)) {
      alert('Please select a JPG, PNG, GIF or WEBP image.');
      return;
    }

    // Validate size up-front so online and offline paths behave identically
    // (matches s3Service.uploadImage's 10MB cap).
    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      alert('File size must be less than 10MB.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Laravel path: uploads/images/properties/{property_id}/reports/mir/
      const pathParts = window.location.pathname.split('/');
      const propertyId = pathParts[2] || pathParts.find((part) => /^\d+$/.test(part));
      const folder = `uploads/images/properties/${propertyId}/reports/mir/`;

      const canBufferOffline = typeof onOfflineSave === 'function';
      const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;

      // Offline (or no connectivity reported): buffer straight away.
      if (!isOnline && canBufferOffline) {
        await bufferOffline(file, folder);
        return;
      }

      try {
        console.log(`📤 [PhotoUpload] Uploading ${fieldName} to S3 path: ${folder}`);
        const s3Key = await uploadOnlineWithTimeout(file, folder);
        console.log(`✅ [S3 UPLOAD] Successfully uploaded ${fieldName} to S3:`, s3Key);
        onUpload(fieldName, s3Key, existingImageName);
        showPreview(file);
      } catch (error) {
        console.error(`❌ [S3 UPLOAD] Failed to upload ${fieldName}:`, error);
        if (canBufferOffline) {
          // Online attempt failed (network/timeout) — buffer for later sync.
          await bufferOffline(file, folder);
        } else {
          // Legacy fallback for screens without offline support: keep the File.
          onUpload(fieldName, file, existingImageName);
          showPreview(file);
        }
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemove = () => {
    onUpload(fieldName, null, '');
    setPreview(null);
    setImageUrl(null);
    if (typeof onPreviewReady === 'function') {
      onPreviewReady(fieldName, null);
    }
    if (isRotationControlled) {
      // Parent owns rotation — they'll reset it; we have no setter for it here.
    } else {
      setInternalRotation(0);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRotate = () => {
    if (isRotationControlled) {
      controlledOnRotate();
    } else {
      setInternalRotation((prev) => (prev + 90) % 360);
    }
  };


  return (
    <div className={styles.photoUpload}>
      <div className={styles.photoContainer}>
        {preview || imageUrl ? (
          <div className={styles.previewContainer}>
            <img
              src={preview || imageUrl}
              alt={label}
              className={styles.preview}
              style={{
                transform: `rotate(${rotation}deg)`,
                cursor: onPreviewClick ? 'zoom-in' : 'default',
              }}
              onClick={onPreviewClick}
              title={onPreviewClick ? 'Click to view larger' : undefined}
              onError={(e) => {
                console.error(`❌ [PhotoUpload] Failed to load image for ${fieldName}:`, e.target.src);
                
                // Prevent infinite loop: only try fallback once
                if (!hasTriedFallback && existingImage && !existingImage.startsWith('http')) {
                  setHasTriedFallback(true);
                  const backendUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/s3/file/${existingImage}`;
                  
                  // Only set new src if it's different from current src
                  if (e.target.src !== backendUrl) {
                    e.target.src = backendUrl;
                    console.log(`🔄 [PhotoUpload] Fallback to backend URL for ${fieldName}:`, backendUrl);
                  } else {
                    // If backend URL also failed, stop trying
                    console.error(`❌ [PhotoUpload] Backend URL also failed for ${fieldName}, stopping retries`);
                    e.target.style.display = 'none'; // Hide broken image
                  }
                } else {
                  // Already tried fallback or no fallback available, stop trying
                  console.error(`❌ [PhotoUpload] Image load failed for ${fieldName}, no more retries`);
                  e.target.style.display = 'none'; // Hide broken image
                }
              }}
            />
            <div className={styles.photoActions}>
              <button
                type="button"
                onClick={handleRotate}
                className={styles.actionButton}
                title="Rotate"
              >
                ↻
              </button>
              <button
                type="button"
                onClick={handleRemove}
                className={styles.actionButton}
                title="Remove"
              >
                ×
              </button>
            </div>
          </div>
        ) : typeof existingImage === 'string' &&
          existingImage.startsWith(OFFLINE_PHOTO_PREFIX) ? (
          <div className={styles.previewContainer}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: 120,
                background: '#eef3fb',
                color: '#2f7ed8',
                borderRadius: 4,
                textAlign: 'center',
                padding: 8,
                fontSize: 13,
              }}
            >
              <div style={{ fontSize: 22 }}>📷</div>
              <div>Saved offline</div>
              <div style={{ fontSize: 11, color: '#6b7c93' }}>
                will upload when online
              </div>
            </div>
            <div className={styles.photoActions}>
              <button
                type="button"
                onClick={handleRemove}
                className={styles.actionButton}
                title="Remove"
              >
                ×
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.uploadArea}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className={styles.fileInput}
              id={`${fieldName}-input`}
              disabled={uploading}
            />
            <label htmlFor={`${fieldName}-input`} className={styles.uploadLabel}>
              {uploading ? (
                <div className={styles.uploadProgress}>
                  <div className={styles.uploadIcon}>⏳</div>
                  <div className={styles.uploadText}>Uploading... {uploadProgress}%</div>
                  <div className={styles.progressBar}>
                    <div 
                      className={styles.progressFill} 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              ) : (
                <>
                  <div className={styles.uploadIcon}>📷</div>
                  <div className={styles.uploadText}>Upload image</div>
                </>
              )}
            </label>
          </div>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.label === nextProps.label &&
    prevProps.fieldName === nextProps.fieldName &&
    prevProps.existingImage === nextProps.existingImage &&
    prevProps.existingImageName === nextProps.existingImageName &&
    prevProps.existingImageUrl === nextProps.existingImageUrl &&
    prevProps.onUpload === nextProps.onUpload &&
    prevProps.onPreviewClick === nextProps.onPreviewClick &&
    prevProps.rotation === nextProps.rotation &&
    prevProps.onRotate === nextProps.onRotate &&
    prevProps.onPreviewReady === nextProps.onPreviewReady &&
    prevProps.onOfflineSave === nextProps.onOfflineSave
  );
});

PhotoUpload.displayName = 'PhotoUpload';

export default PhotoUpload; 